import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from './AdminAuthContext';
import { ACTIVE_STATUSES, UploadQueue, type UploadJob } from '../uploads/uploadQueue';
import { hashFileCrc32 } from '../uploads/hashFile';
import { putFile, registerScans } from '../uploads/transport';
import { queryKeys } from '../api/queryKeys';
import { UploadQueueContext } from './UploadQueueContext';

/** Coalesces the scan refetches triggered as files land. */
const REFETCH_DEBOUNCE_MS = 1_500;

/**
 * Holds the app's one upload queue above the routes. Keeping it here, rather
 * than in the roll cards, is what lets uploads survive the session lapsing:
 * the 401 redirect to the login page unmounts every page, but not this.
 */
export function UploadQueueProvider({
  children,
  queue: injected,
}: {
  children: ReactNode;
  /** For tests: a queue with fake network dependencies. */
  queue?: UploadQueue;
}) {
  const { handleUnauthorized, isAdminAuthenticated } = useAdminAuth();
  const queryClient = useQueryClient();

  // The queue is built once, with the scan-refetch timers beside it so the
  // unmount cleanup can clear them. handleUnauthorized and queryClient are
  // stable (AdminAuthProvider memoises the former), so capturing them here is
  // safe.
  const [{ queue, refetchTimers }] = useState(() => {
    const refetchTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const queue =
      injected ??
      new UploadQueue({
        hash: hashFileCrc32,
        register: registerScans,
        put: putFile,
        onUnauthorized: () => handleUnauthorized(),
        onFileLanded: (orderId) => {
          clearTimeout(refetchTimers.get(orderId));
          refetchTimers.set(
            orderId,
            setTimeout(() => {
              refetchTimers.delete(orderId);
              queryClient.invalidateQueries({ queryKey: queryKeys.admin.order(orderId) });
            }, REFETCH_DEBOUNCE_MS)
          );
        },
      });
    return { queue, refetchTimers };
  });

  const snapshot = useSyncExternalStore(queue.subscribe, queue.getSnapshot);

  // Back from the login page: pick up where the queue stopped. Keyed on the
  // signed-out → signed-in transition, not on "paused while signed in": the
  // queue pauses a render or so before the 401 handler flips the auth flag, and
  // resuming in that gap would send the next file straight into another 401.
  const wasAuthenticated = useRef(isAdminAuthenticated);
  useEffect(() => {
    const signedBackIn = isAdminAuthenticated && !wasAuthenticated.current;
    wasAuthenticated.current = isAdminAuthenticated;
    if (signedBackIn) queue.resume();
  }, [isAdminAuthenticated, queue]);

  // Files held only in this tab's memory are lost with it, so ask first.
  const holdsFiles = snapshot.jobs.some((j) => j.status === 'pending' || ACTIVE_STATUSES.has(j.status));
  useEffect(() => {
    if (!holdsFiles) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [holdsFiles]);

  useEffect(() => () => refetchTimers.forEach((t) => clearTimeout(t)), [refetchTimers]);

  return (
    <UploadQueueContext.Provider value={queue}>
      {children}
      <UploadStatusBanner jobs={snapshot.jobs} authPaused={snapshot.authPaused} />
    </UploadQueueContext.Provider>
  );
}

/**
 * A small fixed notice for uploads the current page doesn't show: those paused
 * for sign-in (seen on the login page) or still running for an order the admin
 * has navigated away from.
 */
function UploadStatusBanner({ jobs, authPaused }: { jobs: readonly UploadJob[]; authPaused: boolean }) {
  const { pathname } = useLocation();
  const active = jobs.filter((j) => ACTIVE_STATUSES.has(j.status));
  if (active.length === 0) return null;

  const orderIds = [...new Set(active.map((j) => j.orderId))];
  const offPage = orderIds.filter((id) => pathname !== `/admin/orders/${id}/upload`);
  if (!authPaused && offPage.length === 0) return null;

  const count = active.length;
  const files = `${count} file${count === 1 ? '' : 's'}`;
  return (
    <div role="status" style={bannerStyle}>
      {authPaused ? (
        <>Your session expired. {files} will carry on uploading once you sign in.</>
      ) : (
        <>
          Uploading {files} in the background.{' '}
          {offPage.length === 1 && (
            <Link to={`/admin/orders/${offPage[0]}/upload`} style={{ color: 'inherit' }}>
              View order
            </Link>
          )}
        </>
      )}
    </div>
  );
}

const bannerStyle: CSSProperties = {
  position: 'fixed',
  right: 16,
  bottom: 16,
  maxWidth: 'calc(100vw - 32px)',
  padding: '0.6rem 0.9rem',
  borderRadius: 6,
  // Inverted, so it stands out from the page in either theme.
  background: 'var(--colour-text-primary)',
  color: 'var(--colour-bg-primary)',
  fontSize: '0.875rem',
  boxShadow: '0 2px 8px var(--colour-shadow-strong)',
  zIndex: 1000,
};
